import type { AppConfig } from './types';
import { ledger17App } from './ledger17';

/**
 * Saldo17 — the Norwegian bookkeeping product. Same engine and SPA as ledger17, sold
 * in kroner as a finished accounting system rather than as a metered API.
 *
 * Brand tokens are ledger17's, deliberately and by reference rather than by copy: the
 * two are one product family and the login page they share (auth-server theme
 * `ledger17`, see sirk-auth-backend `oauth2.client.saldo17.theme`) is themed once. A
 * copy would let the two drift apart silently, which is exactly what a customer moving
 * between app.saldo17.no and the shared login would notice. Give saldo17 its own tokens
 * here the day it gets its own visual identity — nothing else needs to change.
 *
 * The IBM Plex faces the tokens name are declared in `src/styles/fonts.css` and served
 * from `/apps/ledger17/*.woff2`. That path is where the files live, not an app scope —
 * everything under `public/` is served whatever `VITE_APP_ID` is — so saldo17 needs no
 * font copies of its own.
 */
export const saldo17App: AppConfig = {
    id: 'saldo17',
    productName: 'Saldo17',
    htmlTitle: 'Admin - Saldo17',
    // Unlike ledger17, saldo17 has a companion application: this orgadmin instance is
    // the registration and account surface at admin.saldo17.no, while the bookkeeping
    // app itself lives at app.saldo17.no. Both are registered redirect URIs on the
    // `saldo17-spa` OAuth2 client (sirk-auth-backend application.properties).
    appLinkUrl: 'https://app.saldo17.no',
    homepageUrl: 'https://saldo17.no',
    showBackToApp: true,
    logo: { src: '/apps/saldo17/favicon.svg', alt: 'Saldo17' },
    favicon: '/apps/saldo17/favicon.svg',
    brand: ledger17App.brand,
    defaultThemeMode: 'dark',
    // Organisation self-management only, as for ledger17: the bookkeeping domain lives
    // in the saldo17 app itself, not in orgadmin.
    enabledResources: new Set([
        'settings',
        'billing',
        'user',
        'api-key',
        'category/location',
        'category/department',
    ]),
    signup: {
        // Tier ids must match the backend's SubscriptionLevel constants exactly — the id
        // is posted verbatim as `subscriptionLevel` at signup, and a value no App claims
        // creates no entitlement at all (see the ledger17 tiers for that failure mode).
        //
        // Both tiers meter identically to ledger17's, at 100 new / 6 000 stored
        // transactions per step: the ladder sells capability, not volume. Display only —
        // the backend does not meter steps yet.
        tiers: [
            {
                id: 'SALDO17_BASIS',
                i18nKey: 'saldo17Basis',
                // Norway-only, so kroner is the only currency worth carrying.
                prices: { NOK: 199 },
                featureCount: 5,
                highlighted: true,
                metered: { newTransactions: 100, storedTransactions: 6000 },
            },
            {
                // API keys, and with them the MCP/AI surface those keys authenticate.
                // Sold on SALDO17 rank 2 in the backend's READ/WRITE_API_KEYS sellers —
                // which is why SALDO17_BASIS sells the ledger permissions directly
                // instead of including LEDGER_API, whose rank 1 carries API keys.
                id: 'SALDO17_API_AI',
                i18nKey: 'saldo17ApiAi',
                prices: { NOK: 249 },
                featureCount: 4,
                // Basis keeps the badge and the default selection: this is an opt-up.
                highlighted: false,
                metered: { newTransactions: 100, storedTransactions: 6000 },
            },
        ],
        // The NRS ruleset and filings to Altinn/Skatteetaten are Norwegian, and the
        // tiers are priced in kroner alone — so unlike ledger17, which is a bare API
        // sold anywhere, this one restricts. Mirrors `countries.app.saldo17=no` in
        // sirk-orgadmin-backend, which is the check that actually decides.
        countries: ['no'],
    },
    defaultTaxonomies: [],
};
